/**
 * Goods Transfer Request Form Component
 * Create and edit goods transfer requests
 */

import React, { useState, useEffect } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';
import { useLocation } from '../../../contexts/LocationContext';
import SearchableSelect from '../../Common/SearchableSelect';
import inventoryApi from '../../../services/inventoryApi';
import adminApi from '../../../services/adminApi';
import { assetsApi } from '../../../services/financeApi';

const GTRForm = ({ gtr, onSuccess, onCancel }) => {
  const { createGoodsTransferRequest, updateGoodsTransferRequest } = useInventory();
  const { currentLocation } = useLocation();

  // Local state for data loading
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sourceWarehouses, setSourceWarehouses] = useState([]);
  const [destinationWarehouses, setDestinationWarehouses] = useState([]);
  const [loadingSourceWarehouses, setLoadingSourceWarehouses] = useState(false);
  const [loadingDestinationWarehouses, setLoadingDestinationWarehouses] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  const [formData, setFormData] = useState({
    procurementType: 'INVENTORY',
    sourceLocation: '',
    sourceLocationName: '',
    sourceWarehouse: '',
    sourceWarehouseName: '',
    destinationLocation: '',
    destinationLocationName: '',
    destinationWarehouse: '',
    destinationWarehouseName: '',
    priority: 'normal',
    transferReference: '',
    expectedTransferDate: '',
    lineItems: []
  });

  // Location types that represent warehouse-type locations (kept in sync with the
  // admin-service Location model's `isWarehouse` virtual)
  const WAREHOUSE_TYPES = ['warehouse', 'distribution_center', 'cold_storage', 'hazmat_facility', 'transit_hub', 'quarantine_facility', 'returns_center'];

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [stockAvailability, setStockAvailability] = useState({});
  const [loadingStock, setLoadingStock] = useState(false);
  const [assetStockAvailability, setAssetStockAvailability] = useState({});
  const [loadingAssetStock, setLoadingAssetStock] = useState(false);

  // Data loading functions
  // Load non-warehouse locations (headquarters/branch/office/etc.) to act as the parent site
  const loadLocations = async () => {
    try {
      const response = await adminApi.getLocations({ limit: 1000 });
      const allLocations = response.data?.data || response.data || [];
      const nonWarehouseLocations = allLocations.filter(location => !WAREHOUSE_TYPES.includes(location.type));
      setLocations(nonWarehouseLocations);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  };

  // Load warehouse-type locations that are children of the given parent location
  const loadWarehousesForLocation = async (locationId, setter, setLoadingFlag) => {
    if (!locationId) {
      setter([]);
      return;
    }

    try {
      setLoadingFlag(true);
      const response = await adminApi.getLocations({ type: 'warehouse', parentId: locationId, limit: 1000 });
      const warehousesData = response.data?.data || response.data || [];
      setter(warehousesData);
    } catch (error) {
      console.error('Error loading warehouses for location:', error);
      setter([]);
    } finally {
      setLoadingFlag(false);
    }
  };

  const loadItems = async () => {
    try {
      const response = await inventoryApi.getItems({ limit: 1000 });
      const itemsData = response.data || [];
      setItems(itemsData);
    } catch (error) {
      console.error('Error loading items:', error);
      setItems([]);
    }
  };

  // Load fixed assets available for FIXED_ASSET transfers (cross-service, best-effort)
  const loadAssets = async () => {
    try {
      const response = await assetsApi.getAll({ status: 'ACTIVE', limit: 500 });
      setAssets(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Error loading fixed assets:', error);
      setAssets([]);
    }
  };

  // Load stock availability for items in the selected source warehouse
  const loadStockAvailability = async (warehouseId, itemIds = null) => {
    if (!warehouseId) {
      setStockAvailability({});
      return;
    }

    try {
      setLoadingStock(true);
      const response = await inventoryApi.getStockAvailabilityByWarehouse(
        warehouseId,
        itemIds
      );

      if (response.success) {
        // Convert array to object keyed by item ID for easy lookup
        const stockMap = {};
        response.data.forEach(stock => {
          stockMap[stock.item._id] = stock;
        });
        setStockAvailability(stockMap);
      }
    } catch (error) {
      console.error('Error loading stock availability:', error);
      setStockAvailability({});
    } finally {
      setLoadingStock(false);
    }
  };

  // Load fixed-asset stock availability (separate AssetStockLevel ledger) for a warehouse
  const loadAssetStockAvailability = async (warehouseId) => {
    if (!warehouseId) {
      setAssetStockAvailability({});
      return;
    }

    try {
      setLoadingAssetStock(true);
      const response = await inventoryApi.getAssetStockLevels({ warehouse: warehouseId, limit: 500 });
      const levels = response.data || [];
      const stockMap = {};
      levels.forEach(level => {
        stockMap[level.asset] = level;
      });
      setAssetStockAvailability(stockMap);
    } catch (error) {
      console.error('Error loading asset stock availability:', error);
      setAssetStockAvailability({});
    } finally {
      setLoadingAssetStock(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      try {
        await Promise.all([
          loadLocations(),
          loadItems(),
          loadAssets()
        ]);
      } catch (error) {
        console.error('Error loading form data:', error);
        if (error.response?.status === 401) {
          console.error('Authentication failed - please log in again');
          setErrors({ submit: 'Authentication failed. Please log in again.' });
        }
      } finally {
        setDataLoading(false);
      }
    };

    loadData();
  }, []);

  // Load source warehouses when the source location changes
  useEffect(() => {
    loadWarehousesForLocation(formData.sourceLocation, setSourceWarehouses, setLoadingSourceWarehouses);
  }, [formData.sourceLocation]);

  // Load destination warehouses when the destination location changes
  useEffect(() => {
    loadWarehousesForLocation(formData.destinationLocation, setDestinationWarehouses, setLoadingDestinationWarehouses);
  }, [formData.destinationLocation]);

  // Load fixed-asset stock availability when source warehouse changes (FIXED_ASSET transfers only)
  useEffect(() => {
    if (formData.procurementType === 'FIXED_ASSET' && formData.sourceWarehouse) {
      loadAssetStockAvailability(formData.sourceWarehouse);
    } else {
      setAssetStockAvailability({});
    }
  }, [formData.procurementType, formData.sourceWarehouse]);

  // Load stock availability when source warehouse changes
  useEffect(() => {
    if (formData.procurementType === 'INVENTORY' && formData.sourceWarehouse) {
      const itemIds = formData.lineItems
        .filter(item => item.item)
        .map(item => item.item);

      loadStockAvailability(formData.sourceWarehouse, itemIds);
    } else {
      setStockAvailability({});
    }
  }, [formData.procurementType, formData.sourceWarehouse, formData.lineItems]);

  useEffect(() => {
    if (gtr) {
      // Process line items to extract item/asset IDs if they are objects
      const processedLineItems = (gtr.lineItems || []).map(lineItem => ({
        ...lineItem,
        item: lineItem.item?._id || lineItem.item || '', // Extract ID from populated object
        assetId: lineItem.assetId?._id || lineItem.assetId || null,
        assetCode: lineItem.assetCode || '',
        assetName: lineItem.assetName || '',
        itemCode: lineItem.itemCode || lineItem.item?.sku || lineItem.item?.itemCode || '',
        itemDescription: lineItem.itemDescription || lineItem.item?.description || lineItem.item?.name || '',
        uom: lineItem.uom || lineItem.item?.inventory?.unit || '',
        quantityRequested: lineItem.quantityRequested || '',
        unitPrice: lineItem.unitPrice || lineItem.item?.pricing?.unitCost || 0,
        netAmount: lineItem.netAmount || (lineItem.quantityRequested * (lineItem.unitPrice || lineItem.item?.pricing?.unitCost || 0)) || 0
      }));

      setFormData({
        procurementType: gtr.procurementType || 'INVENTORY',
        sourceLocation: gtr.sourceLocation?._id || gtr.sourceLocation || '',
        sourceLocationName: gtr.sourceLocationName || gtr.sourceLocation?.name || '',
        sourceWarehouse: gtr.sourceWarehouse?._id || gtr.sourceWarehouse || '',
        sourceWarehouseName: gtr.sourceWarehouseName || gtr.sourceWarehouse?.name || '',
        destinationLocation: gtr.destinationLocation?._id || gtr.destinationLocation || '',
        destinationLocationName: gtr.destinationLocationName || gtr.destinationLocation?.name || '',
        destinationWarehouse: gtr.destinationWarehouse?._id || gtr.destinationWarehouse || '',
        destinationWarehouseName: gtr.destinationWarehouseName || gtr.destinationWarehouse?.name || '',
        priority: gtr.priority || 'normal',
        transferReference: gtr.transferReference || '',
        expectedTransferDate: gtr.expectedTransferDate ?
          new Date(gtr.expectedTransferDate).toISOString().split('T')[0] : '',
        lineItems: processedLineItems
      });
    } else {
      // Initialize with one empty line item for new requests, defaulting the
      // destination location to wherever the requester is currently logged in —
      // a GTR is always raised BY the destination location asking a source
      // location to issue stock, so it should default to "here".
      setFormData(prev => ({
        ...prev,
        destinationLocation: currentLocation?._id || currentLocation?.id || prev.destinationLocation,
        destinationLocationName: currentLocation?.name || prev.destinationLocationName,
        lineItems: [{
          lineNo: 1,
          item: '',
          assetId: null,
          assetCode: '',
          assetName: '',
          itemCode: '',
          itemDescription: '',
          batchLotNo: '',
          serialNo: '',
          quantityRequested: '',
          uom: '',
          unitPrice: 0,
          netAmount: 0,
          remarks: ''
        }]
      }));
    }
  }, [gtr, currentLocation]);

  const handleProcurementTypeChange = (value) => {
    // Switching type invalidates existing line refs (item vs asset) — reset the items list
    setFormData(prev => ({
      ...prev,
      procurementType: value,
      lineItems: [{
        lineNo: 1,
        item: '',
        assetId: null,
        assetCode: '',
        assetName: '',
        itemCode: '',
        itemDescription: '',
        batchLotNo: '',
        serialNo: '',
        quantityRequested: '',
        uom: '',
        unitPrice: 0,
        netAmount: 0,
        remarks: ''
      }]
    }));
    setErrors(prev => ({ ...prev, lineItems: '' }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLocationChange = (field, locationId) => {
    const selectedLocation = locations.find(l => l._id === locationId);
    const nameField = field === 'sourceLocation' ? 'sourceLocationName' : 'destinationLocationName';
    const warehouseField = field === 'sourceLocation' ? 'sourceWarehouse' : 'destinationWarehouse';
    const warehouseNameField = field === 'sourceLocation' ? 'sourceWarehouseName' : 'destinationWarehouseName';

    setFormData(prev => ({
      ...prev,
      [field]: locationId,
      [nameField]: selectedLocation ? selectedLocation.name : '',
      // Reset the dependent warehouse selection since it belongs to the previous location
      [warehouseField]: '',
      [warehouseNameField]: ''
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleWarehouseChange = (field, warehouseId) => {
    const warehouseList = field === 'sourceWarehouse' ? sourceWarehouses : destinationWarehouses;
    const selectedWarehouse = warehouseList.find(w => w._id === warehouseId);
    const nameField = field === 'sourceWarehouse' ? 'sourceWarehouseName' : 'destinationWarehouseName';

    setFormData(prev => ({
      ...prev,
      [field]: warehouseId,
      [nameField]: selectedWarehouse ? selectedWarehouse.name : ''
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLineItemChange = (index, field, value) => {
    setFormData(prev => {
      const newLineItems = [...prev.lineItems];
      newLineItems[index] = { ...newLineItems[index], [field]: value };

      // Auto-populate item details when an inventory item is selected
      if (field === 'item' && value) {
        const selectedItem = items.find(item => item._id === value);
        if (selectedItem) {
          newLineItems[index] = {
            ...newLineItems[index],
            itemCode: selectedItem.itemCode || selectedItem.sku,
            itemDescription: selectedItem.description || selectedItem.name,
            uom: selectedItem.inventory?.unit,
            unitPrice: selectedItem.pricing?.unitCost || 0
          };

          const quantity = parseFloat(newLineItems[index].quantityRequested) || 0;
          const unitPrice = selectedItem.pricing?.unitCost || 0;
          newLineItems[index].netAmount = quantity * unitPrice;

          if (prev.sourceWarehouse && !stockAvailability[value]) {
            loadStockAvailability(prev.sourceWarehouse, [value]);
          }
        }
      }

      // Auto-populate asset details when a fixed asset is selected
      if (field === 'assetId' && value) {
        const selectedAsset = assets.find(asset => asset._id === value);
        if (selectedAsset) {
          const unitPrice = selectedAsset.purchasePrice || selectedAsset.marketPrice || 0;
          newLineItems[index] = {
            ...newLineItems[index],
            assetCode: selectedAsset.code || '',
            assetName: selectedAsset.name || '',
            itemCode: selectedAsset.code || '',
            itemDescription: selectedAsset.name || '',
            uom: newLineItems[index].uom || 'UNIT',
            unitPrice
          };

          const quantity = parseFloat(newLineItems[index].quantityRequested) || 0;
          newLineItems[index].netAmount = quantity * unitPrice;

          if (prev.sourceWarehouse && !assetStockAvailability[value]) {
            loadAssetStockAvailability(prev.sourceWarehouse);
          }
        }
      }

      // Validate stock availability when quantity changes
      if (field === 'quantityRequested') {
        const quantity = parseFloat(value) || 0;
        const unitPrice = parseFloat(newLineItems[index].unitPrice) || 0;
        newLineItems[index].netAmount = quantity * unitPrice;

        const isFixedAsset = prev.procurementType === 'FIXED_ASSET';
        const referenceId = isFixedAsset ? newLineItems[index].assetId : newLineItems[index].item;
        const stockMap = isFixedAsset ? assetStockAvailability : stockAvailability;
        const stockEntry = referenceId ? stockMap[referenceId] : null;

        if (stockEntry) {
          const availableStock = isFixedAsset ? stockEntry.quantity : stockEntry.stock.available;
          if (quantity > availableStock) {
            setErrors(prev => ({
              ...prev,
              [`lineItem_${index}_quantityRequested`]: `Insufficient stock. Available: ${availableStock}`
            }));
          } else {
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[`lineItem_${index}_quantityRequested`];
              return newErrors;
            });
          }
        }
      }

      // Recalculate net amount when unit price changes
      if (field === 'unitPrice') {
        const quantity = parseFloat(newLineItems[index].quantityRequested) || 0;
        const unitPrice = parseFloat(value) || 0;
        newLineItems[index].netAmount = quantity * unitPrice;
      }

      return { ...prev, lineItems: newLineItems };
    });

    const errorKey = `lineItem_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          lineNo: prev.lineItems.length + 1,
          item: '',
          assetId: null,
          assetCode: '',
          assetName: '',
          itemCode: '',
          itemDescription: '',
          batchLotNo: '',
          serialNo: '',
          quantityRequested: '',
          uom: '',
          unitPrice: 0,
          netAmount: 0,
          remarks: ''
        }
      ]
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index).map((item, i) => ({
        ...item,
        lineNo: i + 1
      }))
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.sourceLocation) newErrors.sourceLocation = 'Source location is required';
    if (!formData.destinationLocation) newErrors.destinationLocation = 'Destination location is required';
    if (!formData.sourceWarehouse) newErrors.sourceWarehouse = 'Source warehouse is required';
    if (!formData.destinationWarehouse) newErrors.destinationWarehouse = 'Destination warehouse is required';
    if (formData.sourceWarehouse === formData.destinationWarehouse) {
      newErrors.destinationWarehouse = 'Destination must be different from source';
    }

    const isFixedAsset = formData.procurementType === 'FIXED_ASSET';

    if (formData.lineItems.length === 0) {
      newErrors.lineItems = 'At least one line item is required';
    } else {
      formData.lineItems.forEach((lineItem, index) => {
        const referenceId = isFixedAsset ? lineItem.assetId : lineItem.item;
        if (!referenceId) {
          newErrors[`lineItem_${index}_item`] = isFixedAsset ? 'Fixed asset is required' : 'Item is required';
        }
        if (!lineItem.quantityRequested || lineItem.quantityRequested <= 0) {
          newErrors[`lineItem_${index}_quantityRequested`] = 'Valid quantity is required';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        ...formData,
        sourceLocationName: formData.sourceLocationName || locations.find(l => l._id === formData.sourceLocation)?.name || '',
        destinationLocationName: formData.destinationLocationName || locations.find(l => l._id === formData.destinationLocation)?.name || '',
        sourceWarehouseName: formData.sourceWarehouseName || sourceWarehouses.find(w => w._id === formData.sourceWarehouse)?.name || '',
        destinationWarehouseName: formData.destinationWarehouseName || destinationWarehouses.find(w => w._id === formData.destinationWarehouse)?.name || '',
        lineItems: formData.lineItems.map((item, index) => ({
          ...item,
          lineNo: index + 1,
          // Exactly one of item/assetId is set, depending on procurementType
          item: formData.procurementType === 'FIXED_ASSET' ? undefined : (item.item || undefined),
          assetId: formData.procurementType === 'FIXED_ASSET' ? (item.assetId || undefined) : undefined,
          quantityRequested: parseFloat(item.quantityRequested),
          unitPrice: parseFloat(item.unitPrice) || 0,
          netAmount: parseFloat(item.netAmount) || 0
        }))
      };

      if (gtr) {
        await updateGoodsTransferRequest(gtr._id, requestData);
      } else {
        await createGoodsTransferRequest(requestData);
      }

      onSuccess();
    } catch (error) {
      console.error('Error submitting GTR:', error);

      let errorMessage = 'Failed to save transfer request';

      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to create transfer requests.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Stock transfers service is not available. Please contact support.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const canEditGTR = (gtrRecord) => {
    // Allow editing for new requests, or existing ones still in draft/requested state
    if (!gtrRecord) return true;
    return ['draft', 'requested'].includes(gtrRecord.status);
  };

  const editable = canEditGTR(gtr);

  const getTotalQuantity = () => formData.lineItems.reduce((sum, item) => sum + (parseFloat(item.quantityRequested) || 0), 0);
  const getTotalAmount = () => formData.lineItems.reduce((sum, item) => sum + (parseFloat(item.netAmount) || 0), 0);

  const canSubmitForm = () => {
    const hasBasicInfo = formData.sourceLocation && formData.sourceWarehouse &&
      formData.destinationLocation && formData.destinationWarehouse;
    const isFixedAsset = formData.procurementType === 'FIXED_ASSET';
    const hasValidItems = formData.lineItems.length > 0 &&
      formData.lineItems.every(item => (isFixedAsset ? item.assetId : item.item) && parseFloat(item.quantityRequested) > 0);
    return hasBasicInfo && hasValidItems && editable;
  };

  const inputCls = (hasError) =>
    `w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-300'} ${!editable ? 'opacity-50 cursor-not-allowed' : ''}`;

  const priorityOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const locationOptions = Array.isArray(locations) ? locations.map(location => ({
    value: location._id,
    label: location.name
  })) : [];

  const sourceWarehouseOptions = Array.isArray(sourceWarehouses) ? sourceWarehouses.map(warehouse => ({
    value: warehouse._id,
    label: warehouse.name
  })) : [];

  const destinationWarehouseOptions = Array.isArray(destinationWarehouses) ? destinationWarehouses.map(warehouse => ({
    value: warehouse._id,
    label: warehouse.name
  })) : [];

  const itemOptions = Array.isArray(items) ? items.map(item => ({
    value: item._id,
    label: `${item.sku} - ${item.name}`
  })) : [];

  const assetOptions = Array.isArray(assets) ? assets.map(asset => ({
    value: asset._id,
    label: `${asset.name || 'Unknown'}${asset.code ? ` (${asset.code})` : ''}${asset.serialNumber ? ` — ${asset.serialNumber}` : ''}`
  })) : [];

  return (
    <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-full px-4 py-6">
        <div className="relative w-full max-w-6xl bg-white rounded-lg shadow-xl">

          {/* Data loading overlay */}
          {dataLoading && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-lg z-10 flex flex-col items-center justify-center gap-3">
              <svg className="animate-spin h-8 w-8 text-indigo-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="text-sm font-medium text-indigo-600">Loading locations and inventory items…</span>
            </div>
          )}

          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-white/20 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">
                  {gtr ? 'Edit Transfer Request' : 'New Transfer Request'}
                </h3>
                {gtr && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    gtr.status === 'approved' || gtr.status === 'fulfilled' ? 'bg-green-500/20 text-green-100' :
                      gtr.status === 'rejected' || gtr.status === 'cancelled' ? 'bg-red-500/20 text-red-100' :
                        gtr.status === 'converted_to_gi' ? 'bg-indigo-500/20 text-indigo-100' :
                          gtr.status === 'partially_fulfilled' ? 'bg-amber-500/20 text-amber-100' :
                            'bg-white/20 text-white'
                    }`}>
                    {(gtr.status || 'draft').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                )}
              </div>
              <button
                onClick={handleCancel}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white">
            <form id="gtr-form" onSubmit={handleSubmit} className="px-4 pt-3 pb-2">

              {/* Error Summary */}
              {errors.submit && (
                <div className="mb-2 bg-red-50 border border-red-200 rounded p-2">
                  <p className="text-xs text-red-700">{errors.submit}</p>
                </div>
              )}

              {/* Procurement Type row */}
              <div className="grid grid-cols-4 gap-2 mb-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Procurement Type <span className="text-red-500">*</span></label>
                  <select
                    value={formData.procurementType}
                    onChange={(e) => handleProcurementTypeChange(e.target.value)}
                    disabled={!editable}
                    className={inputCls(false)}
                  >
                    <option value="INVENTORY">Inventory</option>
                    <option value="FIXED_ASSET">Fixed Asset</option>
                  </select>
                </div>
              </div>

              {/* Location + Warehouse row */}
              <div className="grid grid-cols-4 gap-2 mb-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Source Location <span className="text-red-500">*</span></label>
                  <SearchableSelect
                    value={formData.sourceLocation}
                    onChange={(e) => handleLocationChange('sourceLocation', e.target.value)}
                    options={locationOptions}
                    placeholder="Select location..."
                    error={errors.sourceLocation || ''}
                    disabled={!editable}
                    size="sm"
                  />
                  {locationOptions.length === 0 && (
                    <p className="text-[10px] text-amber-600 mt-0.5">No locations available</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Source Warehouse <span className="text-red-500">*</span></label>
                  <SearchableSelect
                    value={formData.sourceWarehouse}
                    onChange={(e) => handleWarehouseChange('sourceWarehouse', e.target.value)}
                    options={sourceWarehouseOptions}
                    disabled={!formData.sourceLocation || !editable}
                    placeholder={!formData.sourceLocation ? 'Select location first' : (loadingSourceWarehouses ? 'Loading...' : 'Select warehouse...')}
                    error={errors.sourceWarehouse || ''}
                    size="sm"
                  />
                  {formData.sourceLocation && !loadingSourceWarehouses && sourceWarehouseOptions.length === 0 && (
                    <p className="text-[10px] text-amber-600 mt-0.5">No warehouses under this location</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Destination Location <span className="text-red-500">*</span></label>
                  <SearchableSelect
                    value={formData.destinationLocation}
                    onChange={(e) => handleLocationChange('destinationLocation', e.target.value)}
                    options={locationOptions}
                    placeholder="Select location..."
                    error={errors.destinationLocation || ''}
                    disabled={!editable}
                    size="sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Destination Warehouse <span className="text-red-500">*</span></label>
                  <SearchableSelect
                    value={formData.destinationWarehouse}
                    onChange={(e) => handleWarehouseChange('destinationWarehouse', e.target.value)}
                    options={destinationWarehouseOptions}
                    disabled={!formData.destinationLocation || !editable}
                    placeholder={!formData.destinationLocation ? 'Select location first' : (loadingDestinationWarehouses ? 'Loading...' : 'Select warehouse...')}
                    error={errors.destinationWarehouse || ''}
                    size="sm"
                  />
                  {formData.destinationLocation && !loadingDestinationWarehouses && destinationWarehouseOptions.length === 0 && (
                    <p className="text-[10px] text-amber-600 mt-0.5">No warehouses under this location</p>
                  )}
                </div>
              </div>

              {/* Priority + Date + Reference row */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <SearchableSelect
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    options={priorityOptions}
                    disabled={!editable}
                    size="sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Expected Transfer Date</label>
                  <input
                    type="date"
                    value={formData.expectedTransferDate}
                    onChange={(e) => handleInputChange('expectedTransferDate', e.target.value)}
                    disabled={!editable}
                    className={inputCls(false)}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Transfer Reference / Notes</label>
                  <input
                    type="text"
                    value={formData.transferReference}
                    onChange={(e) => handleInputChange('transferReference', e.target.value)}
                    placeholder="Enter reference number or additional notes..."
                    disabled={!editable}
                    className={inputCls(false)}
                  />
                </div>
              </div>

              {/* Line Items Section */}
              <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
                <div className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 px-3 py-1.5">
                  <span className="text-xs font-semibold text-white">
                    Line Items ({formData.lineItems.length})
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-indigo-200 font-normal">
                      Total: {getTotalAmount().toFixed(2)}
                    </span>
                    {editable && (
                      <button
                        type="button"
                        onClick={addLineItem}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-white bg-white/20 hover:bg-white/30 rounded transition-colors"
                      >
                        + Add Row
                      </button>
                    )}
                  </div>
                </div>

                {errors.lineItems && (
                  <p className="text-xs text-red-500 px-3 py-1.5 border-b border-gray-100 bg-red-50">{errors.lineItems}</p>
                )}

                <div className="max-h-[36vh] overflow-y-auto">
                  <table className="min-w-max w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 w-56">
                          {formData.procurementType === 'FIXED_ASSET' ? 'Fixed Asset *' : 'Item *'}
                        </th>
                        <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Qty *</th>
                        <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Available</th>
                        <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">UOM</th>
                        <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">Unit Price</th>
                        <th className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">Net Amount</th>
                        <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {formData.lineItems.map((lineItem, index) => (
                        <tr key={index} className={lineItem.quantityRequested > 0 ? 'bg-indigo-50/60' : 'hover:bg-gray-50'}>
                          <td className="px-2 py-1.5 min-w-[200px]">
                            {formData.procurementType === 'FIXED_ASSET' ? (
                              <SearchableSelect
                                value={lineItem.assetId || ''}
                                onChange={(e) => handleLineItemChange(index, 'assetId', e.target.value || null)}
                                options={assetOptions}
                                placeholder={assetOptions.length > 0 ? 'Select a fixed asset' : 'No fixed assets available'}
                                error={errors[`lineItem_${index}_item`] || ''}
                                disabled={!editable}
                                size="sm"
                              />
                            ) : (
                              <SearchableSelect
                                value={lineItem.item}
                                onChange={(e) => handleLineItemChange(index, 'item', e.target.value)}
                                options={itemOptions}
                                placeholder={itemOptions.length > 0 ? 'Select an item' : 'No items available'}
                                error={errors[`lineItem_${index}_item`] || ''}
                                disabled={!editable}
                                size="sm"
                              />
                            )}
                          </td>

                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min="0.001"
                              step="0.001"
                              value={lineItem.quantityRequested}
                              onChange={(e) => handleLineItemChange(index, 'quantityRequested', e.target.value)}
                              placeholder="0.000"
                              disabled={!editable}
                              className={`${inputCls(!!errors[`lineItem_${index}_quantityRequested`])} w-24 text-center`}
                            />
                            {errors[`lineItem_${index}_quantityRequested`] && (
                              <p className="text-[10px] text-red-500 mt-0.5">{errors[`lineItem_${index}_quantityRequested`]}</p>
                            )}
                          </td>

                          <td className="px-2 py-1.5 text-center text-xs">
                            {(() => {
                              const isFixedAsset = formData.procurementType === 'FIXED_ASSET';
                              const referenceId = isFixedAsset ? lineItem.assetId : lineItem.item;

                              if (!referenceId) return <span className="text-gray-400">—</span>;

                              if (isFixedAsset) {
                                if (loadingAssetStock) return <div className="animate-pulse text-gray-400">...</div>;
                                const stockInfo = assetStockAvailability[referenceId];
                                if (!stockInfo) return <span className="text-red-500">No stock</span>;

                                const available = stockInfo.quantity;
                                return (
                                  <span className={`font-medium ${available === 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {available}
                                  </span>
                                );
                              }

                              if (loadingStock) return <div className="animate-pulse text-gray-400">...</div>;
                              const stockInfo = stockAvailability[referenceId];
                              if (!stockInfo) return <span className="text-red-500">No stock</span>;

                              const available = stockInfo.stock.available;
                              const isLowStock = stockInfo.isLowStock;

                              return (
                                <span className={`font-medium ${available === 0 ? 'text-red-600' :
                                  isLowStock ? 'text-amber-600' :
                                    'text-green-600'
                                  }`}>
                                  {available.toFixed(3)}
                                </span>
                              );
                            })()}
                          </td>

                          <td className="px-2 py-1.5 text-center text-xs text-gray-600">
                            {lineItem.uom || '—'}
                          </td>

                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={lineItem.unitPrice}
                              onChange={(e) => handleLineItemChange(index, 'unitPrice', e.target.value)}
                              placeholder="0.00"
                              disabled={!editable}
                              className={`${inputCls(false)} w-24 text-right`}
                            />
                          </td>

                          <td className="px-2 py-1.5 text-right text-xs font-medium text-gray-900">
                            {lineItem.netAmount ? lineItem.netAmount.toFixed(2) : '—'}
                          </td>

                          <td className="px-2 py-1.5 text-center">
                            {editable && formData.lineItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeLineItem(index)}
                                className="inline-flex items-center p-1 rounded text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                                title="Remove item"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer: Totals | Actions */}
              <div className="flex items-center justify-between gap-4 pt-2 border-t border-gray-100">

                {/* Totals Panel */}
                <div className="w-64 border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                  <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600">
                    <span className="text-[10px] font-bold text-indigo-100 uppercase tracking-wider">Qty {getTotalQuantity().toFixed(3)} | Total</span>
                    <span className="text-sm font-black text-white">{getTotalAmount().toFixed(2)}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={loading}
                    className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  {editable && (
                    <button
                      type="submit"
                      form="gtr-form"
                      disabled={loading || !canSubmitForm()}
                      className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {loading && (
                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      )}
                      {loading ? 'Saving...' : (gtr ? 'Update Request' : 'Create Request')}
                    </button>
                  )}
                </div>
              </div>

            </form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default GTRForm;
